import React, { useState, useEffect, useMemo } from "react";
import { useToolCallAccordion } from "@/hooks/useToolCallAccordion";
import {
  WrenchIcon,
  SearchIcon,
  FileTextIcon,
  DatabaseIcon,
  CloudIcon,
  TerminalSquareIcon,
  EyeIcon,
  FrameIcon,
  ChevronRightIcon,
} from "lucide-react";
import { ApplyFileEditsBlock } from "./ApplyFileEditsBlock";
import { getRandomStatusText } from "./utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

// Utility functions for complex className strings
const getAccordionTriggerClasses = () =>
  cn(
    "flex items-center text-xs font-medium text-secondary-n-400 px-0 py-2",
    "hover:no-underline",
    "[&[data-state=closed]>svg:last-child]:hidden [&[data-state=open]>svg:last-child]:hidden"
  );

const getChevronClasses = (accordionValue: string) =>
  cn(
    "w-3 h-3 text-gray-400 transition-all group-hover:opacity-100",
    accordionValue === "tool-content" ? "rotate-90 opacity-100" : "opacity-0"
  );

const getIconContainerClasses = (accordionValue: string) =>
  cn(
    "flex items-center gap-2 transition-all",
    accordionValue === "tool-content" && "group-hover:ml-0",
    !accordionValue && "ml-[-20px] group-hover:ml-0"
  );

interface ToolCallBlockProps {
  toolName: string;
  args: any;
  status: "in-progress" | "succeeded" | "failed" | "aborted";
  toolResult?: any;
  toolCallId?: string;
  messageId: string;
  isLastMessage?: boolean;
  isChatLoading?: boolean;
}

const AnimatedStatusText = ({ baseText }: { baseText: string }) => {
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev >= 3 ? 1 : prev + 1));
    }, 200);

    return () => clearInterval(interval);
  }, []);

  return (
    <span className="text-xs text-secondary-n-500">
      {baseText}
      {".".repeat(dotCount)}
    </span>
  );
};

export const ToolCallBlock: React.FC<ToolCallBlockProps> = ({
  toolName,
  args,
  status,
  toolResult,
  toolCallId,
  messageId,
  isLastMessage = false,
  isChatLoading = false,
}) => {
  // Use custom hook for accordion state management
  const { accordionValue, setAccordionValue, isOpen } = useToolCallAccordion({
    status,
    isLastMessage,
    isChatLoading,
  });

  if (toolName === "applyFileEditsTool") {
    return (
      <ApplyFileEditsBlock
        args={args}
        status={status}
        toolResult={toolResult}
        toolCallId={toolCallId}
        messageId={messageId}
      />
    );
  }

  const isFailedOrAborted = status === "failed" || status === "aborted";

  const getToolHeading = () => {
    switch (toolName) {
      case "readFilesTool":
        return "Read Files";
      case "globTool":
      case "grepTool":
        return "Searched Files Using Patterns";
      case "supabaseDeployEdgeFunctionTool":
        return "Deploy Edge Function";
      case "supabaseRunMigrationTool":
        return "Run Database Migration";
      case "runTerminalCommandTool":
        return "Run Terminal Command";
      case "restartDevServerTool":
        return "Restart Dev Server";
      case "createStoryBoardTool":
        return "Create Storyboard";
      case "createOrUpdateStoriesTool":
        return "Create/Update Stories";
      default:
        return "Tool Call";
    }
  };

  const stringifyRender = (value: any) => {
    if (typeof value !== "string") {
      try {
        value = JSON.stringify(value);
      } catch (e) {
        console.error("Failed to stringify value:", e, value);
        return value?.toString() || "";
      }
    }
    return value;
  };

  const getFallbackText = () => {
    if (status === "failed") {
      const errorMessage = toolResult?.message || "Tool execution failed";
      return <span className="text-xs text-red-400">{errorMessage}</span>;
    }
    if (status === "aborted") {
      return (
        <span className="text-xs text-red-400">Tool execution aborted</span>
      );
    }
    if (status === "in-progress") {
      return <AnimatedStatusText baseText={getRandomStatusText()} />;
    }
    return (
      <span className="text-xs text-secondary-n-500">
        {getRandomStatusText()}
      </span>
    );
  };

  const renderWithErrorIfFailed = (argumentsContent: React.ReactNode) => {
    if (status === "failed" || status === "aborted") {
      const errorMessage =
        toolResult?.message ||
        (status === "aborted"
          ? "Tool execution aborted"
          : "Tool execution failed");

      return (
        <div className="space-y-1">
          {argumentsContent}
          <div className="text-xs text-red-400">{errorMessage}</div>
        </div>
      );
    }

    // For other statuses, just return the arguments content
    return argumentsContent;
  };

  const getToolContent = () => {
    switch (toolName) {
      case "readFilesTool":
        let filesRead = [];
        try {
          filesRead = (args?.filenames || []).map(stringifyRender);
        } catch (e) {
          console.error("Failed to stringify files:", e, args?.filenames);
          filesRead = [];
        }

        return filesRead.length > 0
          ? renderWithErrorIfFailed(
              <ul className="list-none">
                {filesRead.map((file: string, index: number) => (
                  <li key={index} className="text-xs text-secondary-n-400">
                    {file}
                  </li>
                ))}
              </ul>
            )
          : getFallbackText();

      case "globTool":
        let globPatterns = [];
        try {
          globPatterns = (args?.globPatterns || []).map(stringifyRender);
        } catch (e) {
          console.error(
            "Failed to stringify glob patterns:",
            e,
            args?.globPatterns
          );
          globPatterns = [];
        }

        return globPatterns.length > 0
          ? renderWithErrorIfFailed(
              <ul className="list-none">
                {globPatterns.map((pattern: string, index: number) => (
                  <li key={index} className="text-xs text-secondary-n-400">
                    {pattern}
                  </li>
                ))}
              </ul>
            )
          : getFallbackText();

      case "grepTool":
        let patterns = [];
        try {
          patterns = (args?.grepSearchPatterns || []).map(stringifyRender);
        } catch (e) {
          console.error(
            "Failed to stringify grep patterns:",
            e,
            args?.grepSearchPatterns
          );
          patterns = [];
        }

        return patterns.length > 0
          ? renderWithErrorIfFailed(
              <ul className="list-none">
                {patterns.map((pattern: string, index: number) => (
                  <li key={index} className="text-xs text-secondary-n-400">
                    {pattern}
                  </li>
                ))}
              </ul>
            )
          : getFallbackText();

      case "supabaseDeployEdgeFunctionTool":
        const edgeFunctionPath = args?.relativeEdgeFunctionFilePath || "";
        return edgeFunctionPath
          ? renderWithErrorIfFailed(
              <span className="text-xs text-secondary-n-400">
                {edgeFunctionPath}
              </span>
            )
          : getFallbackText();

      case "supabaseRunMigrationTool":
        const migrationName = args?.relativeMigrationFilePath || "";
        return migrationName
          ? renderWithErrorIfFailed(
              <span className="text-xs text-secondary-n-400">
                {migrationName}
              </span>
            )
          : getFallbackText();

      case "runTerminalCommandTool":
        const terminalCommand = args?.command || "";
        return terminalCommand
          ? renderWithErrorIfFailed(
              <code className="text-xs text-secondary-n-400 bg-secondary-n-900 px-2 py-1 rounded">
                {terminalCommand}
              </code>
            )
          : getFallbackText();

      case "restartDevServerTool":
        return renderWithErrorIfFailed(
          <span className="text-xs text-secondary-n-400">
            Restarted Dev Server
          </span>
        );

      case "createStoryBoardTool":
        const componentName = args?.componentName || "";
        return componentName
          ? renderWithErrorIfFailed(
              <span className="text-xs text-secondary-n-400">
                Component: {componentName}
              </span>
            )
          : getFallbackText();

      case "createOrUpdateStoriesTool":
        const storyComponentName = args?.componentName || "";
        const command = args?.command || "";
        const storyFilePath = args?.storyFilePath || "";

        return renderWithErrorIfFailed(
          <div className="text-xs text-secondary-n-400 space-y-1">
            {storyComponentName && (
              <div>
                Component:{" "}
                <span className="font-medium">{storyComponentName}</span>
              </div>
            )}
            {command && (
              <div>
                Action:{" "}
                <span className="font-medium">
                  {command === "create" ? "Created" : "Updated"}
                </span>
              </div>
            )}
            {storyFilePath && (
              <div>
                Path: <span className="font-mono">{storyFilePath}</span>
              </div>
            )}
          </div>
        );

      default:
        return renderWithErrorIfFailed(
          <pre className="text-xs text-secondary-n-400 whitespace-pre-wrap">
            {JSON.stringify(args, null, 2)}
          </pre>
        );
    }
  };

  const toolIcon = useMemo(() => {
    const getToolSpecificIcon = () => {
      switch (toolName) {
        case "readFilesTool":
          return EyeIcon;
        case "globTool":
        case "grepTool":
          return SearchIcon;
        case "supabaseDeployEdgeFunctionTool":
          return CloudIcon;
        case "supabaseRunMigrationTool":
          return DatabaseIcon;
        case "runTerminalCommandTool":
        case "restartDevServerTool":
          return TerminalSquareIcon;
        case "createStoryBoardTool":
          return FrameIcon;
        case "createOrUpdateStoriesTool":
          return FileTextIcon;
        default:
          return WrenchIcon;
      }
    };

    const IconComponent = getToolSpecificIcon();

    return (
      <IconComponent
        className={`w-4 h-4 transition-all ${
          isFailedOrAborted ? "text-red-500" : "text-secondary-n-400"
        }`}
      />
    );
  }, [toolName, isFailedOrAborted]);

  return (
    <div className="my-0.5 group">
      <Accordion
        type="single"
        value={accordionValue}
        onValueChange={setAccordionValue}
        collapsible
      >
        <AccordionItem value="tool-content" className="border-none">
          <AccordionTrigger className={getAccordionTriggerClasses()}>
            <div className="flex items-center gap-2">
              <ChevronRightIcon className={getChevronClasses(accordionValue)} />
              <div className={getIconContainerClasses(accordionValue)}>
                {toolIcon}
                <span className="font-semibold text-xs">
                  {getToolHeading()}
                </span>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-2 transition-all">
            <div className="relative ml-6">
              <div className="absolute left-[-18px] top-0 bottom-0 w-px bg-secondary-n-700"></div>
              {getToolContent()}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
